import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, tap } from 'rxjs/operators';

import { SnackBarType } from 'src/app/core/models/snack-bar.models';

import { injectCatchHttpError } from '../../../../core/errors/catch-http-error.util';
import { injectRedirectIfMissing } from '../../../../core/errors/redirect-if-missing.util';
import { SNACKBAR_OK_ACTION, SnackBarService } from '../../../../core/services/snack-bar/snack-bar.service';
import { ApiProjectsService } from '../../../services/api/api-projects.service';
import * as processActions from './process.actions';

@Injectable()
export class ProcessEffects {
  private apiProjects = inject(ApiProjectsService);
  private router = inject(Router);
  private snackBarService = inject(SnackBarService);
  private readonly redirectIfMissing = injectRedirectIfMissing();
  private readonly catchHttpError = injectCatchHttpError();

  private readonly actions$ = inject<Actions>(Actions);
  getProcesses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.loadProcesses),
      switchMap(({ params }) =>
        this.apiProjects.getProcesses(params).pipe(
          map(processes => processActions.loadProcessesSuccess({ processes })),
          this.catchHttpError(() => processActions.loadProcessesFail()),
        ),
      ),
    ),
  );

  getProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.loadProcess),
      switchMap(({ id, originServer }) =>
        this.apiProjects.getProcess(id, originServer).pipe(
          map(process => processActions.loadProcessSuccess({ process })),
          this.catchHttpError(error => processActions.loadProcessFail({ error })),
        ),
      ),
    ),
  );

  updateProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.updateProcess),
      switchMap(({ id, params }) =>
        this.apiProjects.updateProcess(id, params).pipe(
          map(process => processActions.updateProcessSuccess({ process })),
          this.catchHttpError(() => processActions.updateProcessFail()),
        ),
      ),
    ),
  );

  deleteProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.deleteProcess),
      switchMap(({ process }) => {
        const { id, project } = process;
        return this.apiProjects.deleteProcess(id).pipe(
          map(() => {
            this.snackBarService.show(SnackBarType.Success, 'PROCESS.WAS_DELETED', SNACKBAR_OK_ACTION);
            return processActions.deleteProcessSuccess({ projectId: project.id });
          }),
          this.catchHttpError(() => processActions.deleteProcessFail()),
        );
      }),
    ),
  );

  createProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.createProcess),
      switchMap(({ process }) =>
        this.apiProjects.createProcess(process).pipe(
          map(process => processActions.createProcessSuccess({ process })),
          this.catchHttpError(() => processActions.createProcessFail()),
        ),
      ),
    ),
  );

  redirectAfterCreation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(processActions.createProcessSuccess),
        tap(({ process }) => {
          void this.router.navigate(['/process', process.id]);
        }),
      ),
    { dispatch: false },
  );

  redirectAfterDeletion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(processActions.deleteProcessSuccess),
        tap(() => {
          void this.router.navigate(['/home', 'processes']);
        }),
      ),
    { dispatch: false },
  );

  handleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(processActions.loadProcessFail),
        tap(({ error }) => {
          this.redirectIfMissing(error);
        }),
      ),
    { dispatch: false },
  );
}
